import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import { CircularProgress } from "@mui/material";
import { closeSnackbar } from "notistack";
import { useQueryClient } from "react-query";

import { useDeals, useUserAccount } from "../../CustomHooks/Queries";
import {
  useEmailChangeRequestVerification,
  useEmailChangeVerification,
  useUpdateEmailAddress,
  usePhoneNumberChangeRequestVerification,
  usePhoneNumberChangeVerification,
  useUpdatePhoneNumber,
} from "../../CustomHooks/Mutations";

import { useAppUtilityContext } from "../../Contexts/AppUtilityContext";

import {
  capitaliseString,
  transformCountryCodeFormatToUSNumber,
  transformUSNumberToCountryCodeFormat,
  dispatchErrorToast,
  dispatchSuccessToast,
} from "../../Utils/helpers";

import { APIQueryKeys } from "../../Utils/constants";

import {
  ProfileOutline,
  Calendar,
  Phone,
  Mail,
  Pin,
  SupportThin,
  ChevronLeft,
  Edit,
} from "../../Assets/Icons";

import "../Profile/profile.scss";
import "../Profile/profile-responsive.scss";

const RowChevron = () => (
  <svg className="pc-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6 4l4 4-4 4" stroke="#8B8FA3" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function InlineEditField({ label, value, icon: Icon, onSave, isLoading, type = "text" }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const handleStartEdit = () => {
    setDraft(value);
    setOtp("");
    setOtpStep(false);
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setOtpStep(false);
    setOtp("");
  };

  const handleSubmit = () => {
    onSave({
      draft,
      otp,
      otpStep,
      setOtpStep,
      onDone: handleCancel,
    });
  };

  const isDirty = draft !== value && draft.length > 0;

  if (!editing) {
    return (
      <div className="pc-row" onClick={handleStartEdit}>
        <div className="pc-icon"><Icon /></div>
        <div className="pc-text">
          <span className="pc-label">{label}</span>
          <span className="pc-sub">{value}</span>
        </div>
        <Edit className="pc-edit" />
      </div>
    );
  }

  return (
    <div className="pc-row pc-row-static">
      <div className="pc-icon"><Icon /></div>
      <div className="pc-inline-edit">
        <span className="pc-sub">{label}</span>
        {!otpStep ? (
          <>
            <input
              ref={inputRef}
              className="pc-inline-input"
              type={type}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && handleCancel()}
            />
            <div className="pc-inline-actions">
              <button
                className="pc-inline-btn pc-inline-save"
                disabled={!isDirty || isLoading}
                onClick={handleSubmit}
              >
                {isLoading ? <CircularProgress size={12} /> : "Verify"}
              </button>
              <button className="pc-inline-btn pc-inline-cancel" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="pc-inline-otp-prompt">Enter the code sent to {draft}</p>
            <input
              ref={inputRef}
              className="pc-inline-input"
              type="text"
              value={otp}
              placeholder="Verification code"
              onChange={(e) => setOtp(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && handleCancel()}
            />
            <div className="pc-inline-actions">
              <button
                className="pc-inline-btn pc-inline-save"
                disabled={!otp || isLoading}
                onClick={handleSubmit}
              >
                {isLoading ? <CircularProgress size={12} /> : "Update"}
              </button>
              <button className="pc-inline-btn pc-inline-cancel" onClick={handleCancel}>
                Cancel
              </button>
            </div>
            <button
              className="pc-inline-resend"
              onClick={() => {
                setOtp("");
                onSave({ draft, otp: "", otpStep: false, setOtpStep, onDone: handleCancel });
              }}
            >
              Resend code
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function PersonalInformation() {
  const navigate = useNavigate();
  const { show: showIntercom, update } = useIntercom();
  const queryClient = useQueryClient();
  const { onUserCredentialsUpdate } = useAppUtilityContext();

  const { isLoading: isLoadingDeals, data: deals } = useDeals();
  const { isLoading: isLoadingUserAccountData, data: userAccountData } = useUserAccount();

  useEffect(() => {
    update({
      userId: userAccountData?.data?.data?.user?._id,
      email: userAccountData?.data?.data?.user?.email,
      phone: userAccountData?.data?.data?.user?.phoneNumber,
    });
  }, [userAccountData, update]);

  const user = useMemo(() => userAccountData?.data?.data?.user ?? {}, [userAccountData]);
  const lastName = useMemo(() => capitaliseString(user?.lastName), [user]);
  const phoneNumber = useMemo(() => transformCountryCodeFormatToUSNumber(user?.phoneNumber ?? ""), [user]);

  const addressLine = useMemo(() => {
    return `${user?.address1 ?? ""} ${user?.address2 ?? ""}`.trim();
  }, [user]);

  const cityLine = useMemo(() => {
    return `${!!user?.city ? `${user.city},` : ""} ${user?.state ?? ""} ${user?.zipCode ?? ""}`.trim();
  }, [user]);

  const dateOfBirth = useMemo(() => {
    const dateStr = deals?.data?.data?.currentDeal?.birthdate ?? "";
    if (dateStr === "") return "";
    const dateObj = new Date(dateStr);
    const day = dateObj.getUTCDate();
    const month = dateObj.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
    const year = dateObj.getUTCFullYear();
    return `${month} ${day}, ${year}`;
  }, [deals]);

  const { isLoading: isRequestingEmailOtp, mutate: RequestEmailOtp } = useEmailChangeRequestVerification({
    onSuccess: () => dispatchSuccessToast("Verification code sent"),
    onError: (err) => dispatchErrorToast(err?.response?.data?.error?.msg),
  });

  const { isLoading: isVerifyingEmail, mutate: VerifyEmailOtp } = useEmailChangeVerification({
    onSuccess: (data) => {
      onUserCredentialsUpdate(data);
      queryClient.refetchQueries(APIQueryKeys.userAccount);
    },
    onError: () => dispatchErrorToast("Invalid verification code"),
  });

  const { isLoading: isPatchingEmail, mutate: PatchEmail } = useUpdateEmailAddress({
    onSuccess: () => dispatchSuccessToast("Email updated successfully"),
    onError: () => dispatchErrorToast("Unable to update email"),
  });

  const { isLoading: isRequestingPhoneOtp, mutate: RequestPhoneOtp } = usePhoneNumberChangeRequestVerification({
    onSuccess: () => dispatchSuccessToast("Verification code sent"),
    onError: (err) => dispatchErrorToast(err?.response?.data?.error?.msg),
  });

  const { isLoading: isVerifyingPhone, mutate: VerifyPhoneOtp } = usePhoneNumberChangeVerification({
    onSuccess: (data) => {
      onUserCredentialsUpdate(data);
      queryClient.refetchQueries(APIQueryKeys.userAccount);
    },
    onError: () => dispatchErrorToast("Invalid verification code"),
  });

  const { isLoading: isPatchingPhone, mutate: PatchPhone } = useUpdatePhoneNumber({
    onSuccess: () => dispatchSuccessToast("Phone number updated successfully"),
    onError: () => dispatchErrorToast("Unable to update phone number"),
  });

  const emailLoading = isRequestingEmailOtp || isVerifyingEmail || isPatchingEmail;
  const phoneLoading = isRequestingPhoneOtp || isVerifyingPhone || isPatchingPhone;

  const handleEmailSave = useCallback(({ draft, otp, otpStep, setOtpStep, onDone }) => {
    closeSnackbar();
    if (!otpStep) {
      RequestEmailOtp({ email: draft }, {
        onSuccess: () => setOtpStep(true),
      });
    } else {
      VerifyEmailOtp({ email: draft, phoneNumberCode: otp }, {
        onSuccess: (data) => {
          onUserCredentialsUpdate(data);
          queryClient.refetchQueries(APIQueryKeys.userAccount);
          PatchEmail({ email: draft }, { onSuccess: onDone });
        },
      });
    }
  }, [RequestEmailOtp, VerifyEmailOtp, PatchEmail, onUserCredentialsUpdate, queryClient]);

  const handlePhoneSave = useCallback(({ draft, otp, otpStep, setOtpStep, onDone }) => {
    closeSnackbar();
    const payload = { phoneNumber: transformUSNumberToCountryCodeFormat(draft) };
    if (!otpStep) {
      RequestPhoneOtp(payload, {
        onSuccess: () => setOtpStep(true),
      });
    } else {
      VerifyPhoneOtp({ ...payload, phoneNumberCode: otp }, {
        onSuccess: (data) => {
          onUserCredentialsUpdate(data);
          queryClient.refetchQueries(APIQueryKeys.userAccount);
          PatchPhone({ phone: transformUSNumberToCountryCodeFormat(draft) }, { onSuccess: onDone });
        },
      });
    }
  }, [RequestPhoneOtp, VerifyPhoneOtp, PatchPhone, onUserCredentialsUpdate, queryClient]);

  const isLoading = isLoadingUserAccountData || isLoadingDeals;

  return (
    <div className="profile-overlay">
      <div className="profile-card">
        <div className="pc-header">
          <button className="pc-back" onClick={navigate.bind(this, -1)} aria-label="Go back">
            <ChevronLeft />
          </button>
          <span className="pc-title">Personal Information</span>
        </div>

        {isLoading ? (
          <div className="loading-content">
            <CircularProgress />
          </div>
        ) : (
          <>
            <div className="pc-section-header">Details</div>

            <div className="pc-row pc-row-static">
              <div className="pc-icon"><ProfileOutline /></div>
              <div className="pc-text">
                <span className="pc-sub">First Name</span>
                <span className="pc-label">{user?.firstName}</span>
              </div>
            </div>

            <div className="pc-row pc-row-static">
              <div className="pc-icon"><ProfileOutline /></div>
              <div className="pc-text">
                <span className="pc-sub">Last Name</span>
                <span className="pc-label">{lastName}</span>
              </div>
            </div>

            <div className="pc-row pc-row-static">
              <div className="pc-icon"><Calendar /></div>
              <div className="pc-text">
                <span className="pc-sub">Date of Birth</span>
                <span className="pc-label">{dateOfBirth}</span>
              </div>
            </div>

            <div className="pc-section-header">Contact</div>

            <InlineEditField
              label="Phone"
              value={phoneNumber}
              icon={Phone}
              onSave={handlePhoneSave}
              isLoading={phoneLoading}
              type="tel"
            />

            <InlineEditField
              label="Email"
              value={user?.email ?? ""}
              icon={Mail}
              onSave={handleEmailSave}
              isLoading={emailLoading}
              type="email"
            />

            <div className="pc-section-header">Location</div>

            <div className="pc-row pc-row-static">
              <div className="pc-icon"><Pin /></div>
              <div className="pc-text">
                <span className="pc-label">{addressLine || "No address"}</span>
                <span className="pc-sub">{cityLine}</span>
              </div>
            </div>

            <div className="pc-row" onClick={showIntercom}>
              <div className="pc-icon"><SupportThin /></div>
              <div className="pc-text">
                <span className="pc-label">Contact Support</span>
                <span className="pc-sub">Need to update something?</span>
              </div>
              <RowChevron />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
